
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/common/UserAvatar";
import { RelationshipType } from "@/types/dashboard";
import { Heart, Users, User, Briefcase, UserRound } from "lucide-react";

// Helper function to get the relationship icon
const getRelationshipIcon = (type?: RelationshipType) => {
  if (!type) return null;
  
  switch (type) {
    case "Spouse":
    case "Partner":
      return <Heart className="h-4 w-4 text-rose-500" />;
    case "Parent":
    case "Sibling":
    case "Family":
      return <Users className="h-4 w-4 text-indigo-500" />;
    case "Friend":
      return <UserRound className="h-4 w-4 text-emerald-500" />;
    case "Colleague":
    case "Client":
    case "Mentor":
    case "Professional":
      return <Briefcase className="h-4 w-4 text-amber-500" />;
    default:
      return <User className="h-4 w-4 text-gray-500" />;
  }
};

interface SyncedContactsSelectionProps {
  contacts: Array<{id: string, name: string, avatar?: string}>;
  onContactSelect: (contactData: {
    id: string;
    name: string;
    relationshipType?: RelationshipType;
    interests: string[];
    note?: string;
  }) => void;
}

const SyncedContactsSelection: React.FC<SyncedContactsSelectionProps> = ({ 
  contacts, 
  onContactSelect 
}) => {
  const [selectedContactId, setSelectedContactId] = useState<string>(contacts[0]?.id || "");
  const [relationshipType, setRelationshipType] = useState<RelationshipType | "">("");
  const [interests, setInterests] = useState("");
  const [note, setNote] = useState("");

  const selectedContact = contacts.find(c => c.id === selectedContactId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContactId || !selectedContact) return;
    
    const interestsList = interests
      .split(',')
      .map(interest => interest.trim())
      .filter(interest => interest.length > 0);
    
    onContactSelect({
      id: selectedContactId,
      name: selectedContact.name,
      relationshipType: relationshipType as RelationshipType | undefined,
      interests: interestsList,
      note: note.trim().length > 0 ? note : undefined
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="select-contact">Select a contact to add</Label>
        <Select
          value={selectedContactId}
          onValueChange={setSelectedContactId}
        >
          <SelectTrigger id="select-contact">
            <SelectValue placeholder="Select a contact" />
          </SelectTrigger>
          <SelectContent>
            {contacts.map(contact => (
              <SelectItem key={contact.id} value={contact.id}>
                <div className="flex items-center gap-2">
                  <UserAvatar src={contact.avatar} name={contact.name} size="small" />
                  <span>{contact.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {selectedContactId && (
        <>
          <div className="space-y-2">
            <Label htmlFor="contact-relationship">How do you know them?</Label>
            <Select
              value={relationshipType}
              onValueChange={(value) => setRelationshipType(value as RelationshipType)}
            >
              <SelectTrigger id="contact-relationship" className="w-full">
                <SelectValue placeholder="Select relationship">
                  {relationshipType && (
                    <div className="flex items-center gap-2">
                      {getRelationshipIcon(relationshipType as RelationshipType)}
                      <span>{relationshipType}</span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Spouse">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-rose-500" />
                    <span>Spouse</span>
                  </div>
                </SelectItem>
                <SelectItem value="Partner">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-rose-500" />
                    <span>Partner</span>
                  </div>
                </SelectItem>
                <SelectItem value="Friend">
                  <div className="flex items-center gap-2">
                    <UserRound className="h-4 w-4 text-emerald-500" />
                    <span>Friend</span>
                  </div>
                </SelectItem>
                <SelectItem value="Family">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-indigo-500" />
                    <span>Family</span>
                  </div>
                </SelectItem>
                <SelectItem value="Parent">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-indigo-500" />
                    <span>Parent</span>
                  </div>
                </SelectItem>
                <SelectItem value="Sibling">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-indigo-500" />
                    <span>Sibling</span>
                  </div>
                </SelectItem>
                <SelectItem value="Colleague">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-amber-500" />
                    <span>Colleague</span>
                  </div>
                </SelectItem>
                <SelectItem value="Client">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-amber-500" />
                    <span>Client</span>
                  </div>
                </SelectItem>
                <SelectItem value="Professional">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-amber-500" />
                    <span>Professional</span>
                  </div>
                </SelectItem>
                <SelectItem value="Mentor">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-amber-500" />
                    <span>Mentor</span>
                  </div>
                </SelectItem>
                <SelectItem value="Other">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span>Other</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
      
          <div className="space-y-2">
            <Label htmlFor="contact-interests">What are they interested in?</Label>
            <Input
              id="contact-interests"
              placeholder="Cooking, hiking, jazz, etc. (comma-separated)"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-note">Add a note (optional)</Label>
            <Textarea
              id="contact-note"
              placeholder="Any details you want to remember"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
        </>
      )}
      
      <Button type="submit" className="w-full">
        Add {selectedContact?.name || "Contact"}
      </Button>
    </form>
  );
};

export default SyncedContactsSelection;
